import {
  BrowserAuthError,
  InteractionRequiredAuthError,
  LogLevel,
  PublicClientApplication,
  type AccountInfo,
  type Configuration,
} from '@azure/msal-browser';

declare global {
  interface Window {
    /// Lo define /config.js, que arma el backend al arrancar (ver backend/src/index.ts).
    __CONFIG_EVENTOS__?: { tenantId?: string; clientId?: string };
  }
}

// Primero la configuración que manda el backend en runtime (la imagen de Docker
// no trae datos del tenant); si no está, la del .env con el que se compiló.
const enRuntime = window.__CONFIG_EVENTOS__;
const tenantId =
  enRuntime?.tenantId || (import.meta.env.VITE_ENTRA_TENANT_ID as string | undefined);
const clientId =
  enRuntime?.clientId || (import.meta.env.VITE_ENTRA_CLIENT_ID as string | undefined);

/// Modo de desarrollo sin login de Microsoft (VITE_AUTH_DEV=1).
/// Tiene que ir acompañado de AUTH_DEV=true en backend/.env.
/// Nunca se compila así para producción: el build de Vite toma el .env del
/// entorno donde se buildea, y el backend aborta si ve AUTH_DEV en producción.
export const modoDev = import.meta.env.VITE_AUTH_DEV === '1';

/// Si falta la configuración se muestra una pantalla explicándolo en vez de
/// romper con un error críptico de MSAL.
export const configuracionCompleta = modoDev || Boolean(tenantId && clientId);

/*
  El SPA y la API son el mismo registro de aplicación, así que no hace falta
  exponer una API ni definir un scope propio: se manda el ID token, cuyo `aud`
  ya es el client id de esta app, y el backend lo valida contra las JWKS del
  tenant.

  Si algún día otra aplicación tiene que consumir esta API, ahí sí habría que
  volver a "Expose an API" con un scope y pedir un access token, porque
  aparece un tercero al que hay que expresarle el consentimiento.
*/
const ALCANCES = ['openid', 'profile', 'offline_access'];

const configuracion: Configuration = {
  auth: {
    clientId: clientId ?? '00000000-0000-0000-0000-000000000000',
    authority: `https://login.microsoftonline.com/${tenantId ?? 'common'}`,
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
    navigateToLoginRequestUrl: false,
  },
  cache: {
    // sessionStorage para que cerrar la pestaña cierre la sesión.
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      logLevel: LogLevel.Error,
      loggerCallback: (nivel, mensaje, contieneDatosPersonales) => {
        if (contieneDatosPersonales) return;
        if (nivel === LogLevel.Error) console.error('[msal]', mensaje);
      },
    },
  },
};

export const msal = new PublicClientApplication(configuracion);

let inicializado: Promise<AccountInfo | null> | null = null;

/// Inicializa MSAL y resuelve el retorno del redirect. Idempotente: se puede
/// llamar varias veces (React 18 monta dos veces en desarrollo).
export function iniciarMsal(): Promise<AccountInfo | null> {
  inicializado ??= (async () => {
    await msal.initialize();
    const resultado = await msal.handleRedirectPromise();

    if (resultado?.account) {
      msal.setActiveAccount(resultado.account);
      return resultado.account;
    }

    const cuenta = msal.getActiveAccount() ?? msal.getAllAccounts()[0] ?? null;
    if (cuenta) msal.setActiveAccount(cuenta);
    return cuenta;
  })();

  return inicializado;
}

export async function iniciarSesion(): Promise<void> {
  if (modoDev) {
    window.location.reload();
    return;
  }
  await msal.loginRedirect({ scopes: ALCANCES, prompt: 'select_account' });
}

export async function cerrarSesion(): Promise<void> {
  if (modoDev) {
    window.location.reload();
    return;
  }
  await msal.logoutRedirect({ account: msal.getActiveAccount() ?? undefined });
}

/// ID token para mandarle a la API. Se renueva en silencio y solo manda al
/// usuario a Entra si hace falta interacción (MFA, sesión vencida).
export async function tokenParaApi(): Promise<string> {
  // En modo dev el backend ni mira la cabecera, pero se manda algo para que
  // el resto del cliente HTTP funcione sin ramas especiales.
  if (modoDev) return 'dev';

  const cuenta = msal.getActiveAccount();
  if (!cuenta) throw new Error('No hay sesión activa.');

  try {
    const { idToken } = await msal.acquireTokenSilent({ scopes: ALCANCES, account: cuenta });
    if (!idToken) throw new Error('Entra no devolvió el ID token.');
    return idToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError || error instanceof BrowserAuthError) {
      await msal.acquireTokenRedirect({ scopes: ALCANCES, account: cuenta });
    }
    throw error;
  }
}
